export type LoyaltyProgramCategory = 'airline' | 'hotel' | 'ota';

export interface LoyaltyProgramDefinition {
  name: string;
  category: LoyaltyProgramCategory;
  /** Legacy names from older payloads (parenthetical or shortened forms). */
  aliases?: Array<string>;
  tiers: Array<string>;
}

export const LOYALTY_PROGRAM_CATEGORY_ORDER: Array<LoyaltyProgramCategory> = ['airline', 'hotel', 'ota'];

export const LOYALTY_PROGRAM_CATEGORY_LABELS: Record<LoyaltyProgramCategory, string> = {
  airline: 'Airlines',
  hotel: 'Hotels',
  ota: 'OTAs',
};

const AIRLINE_PROGRAMS: Array<LoyaltyProgramDefinition> = [
  {
    name: 'Air Canada Aeroplan',
    category: 'airline',
    aliases: ['Aeroplan', 'Air Canada'],
    tiers: ['25K', '35K', '50K', '75K', 'Super Elite'],
  },
  {
    name: 'Air France-KLM Flying Blue',
    category: 'airline',
    aliases: ['Flying Blue', 'Air France-KLM'],
    tiers: ['Explorer', 'Silver', 'Gold', 'Platinum', 'Ultimate'],
  },
  {
    name: 'Air New Zealand Koru',
    category: 'airline',
    aliases: ['Koru'],
    tiers: ['Koru Bronze', 'Koru Silver', 'Koru Gold', 'Koru Platinum', 'Koru Black'],
  },
  {
    name: 'All Nippon Airways ANA Mileage Club',
    category: 'airline',
    aliases: ['ANA Mileage Club', 'All Nippon Airways', 'ANA'],
    tiers: ['Bronze', 'Platinum', 'Diamond'],
  },
  {
    name: 'Alaska Airlines Atmos Rewards',
    category: 'airline',
    aliases: ['Alaska Airlines', 'Atmos Rewards'],
    tiers: ['Silver', 'Gold', 'Platinum', 'Titanium'],
  },
  {
    name: 'American Airlines AAdvantage',
    category: 'airline',
    aliases: ['American Airlines (AAdvantage)'],
    tiers: ['Member', 'Gold', 'Platinum', 'Platinum Pro', 'Executive Platinum', 'ConciergeKey'],
  },
  {
    name: 'Cathay Pacific Asia Miles',
    category: 'airline',
    aliases: ['Cathay Pacific (Asia Miles)'],
    tiers: ['Green', 'Silver', 'Gold', 'Diamond'],
  },
  {
    name: 'Delta Air Lines SkyMiles',
    category: 'airline',
    aliases: ['Delta Air Lines (SkyMiles)', 'Delta Airlines SkyMiles', 'Delta Airlines (SkyMiles)'],
    tiers: ['Member', 'Silver', 'Gold', 'Platinum', 'Diamond'],
  },
  {
    name: 'Emirates Skywards',
    category: 'airline',
    aliases: ['Emirates'],
    tiers: ['Blue', 'Silver', 'Gold', 'Platinum'],
  },
  {
    name: 'Etihad Airways Etihad Guest',
    category: 'airline',
    aliases: ['Etihad', 'Etihad Guest', 'Etihad Airways'],
    tiers: ['Bronze', 'Silver', 'Gold', 'Platinum', 'Emerald'],
  },
  {
    name: 'GOL Smiles',
    category: 'airline',
    aliases: ['GOL'],
    tiers: ['Smiles', 'Silver', 'Gold', 'Diamond'],
  },
  {
    name: 'Hong Kong Airlines Fortune Wings Club',
    category: 'airline',
    aliases: ['Fortune Wings Club', 'Hong Kong Airlines'],
    tiers: ['Fortune Wings', 'Select', 'Silver', 'Gold', 'Platinum'],
  },
  {
    name: 'Japan Airlines JAL Mileage Bank',
    category: 'airline',
    aliases: ['JAL Mileage Bank', 'Japan Airlines', 'JAL'],
    tiers: ['Crystal', 'Sapphire', 'JGC Premier', 'Diamond'],
  },
  {
    name: 'JetBlue TrueBlue',
    category: 'airline',
    aliases: ['JetBlue', 'TrueBlue'],
    tiers: ['Mosaic 1', 'Mosaic 2', 'Mosaic 3', 'Mosaic 4'],
  },
  {
    name: 'Lufthansa Miles & More',
    category: 'airline',
    aliases: ['Miles & More', 'Lufthansa'],
    tiers: ['Member', 'Frequent Traveller', 'Senator', 'HON Circle'],
  },
  {
    name: 'Qantas Frequent Flyer',
    category: 'airline',
    aliases: ['Qantas'],
    tiers: ['Bronze', 'Silver', 'Gold', 'Platinum', 'Platinum One'],
  },
  {
    name: 'Qatar Airways Privilege Club',
    category: 'airline',
    aliases: ['Qatar Airways', 'Privilege Club'],
    tiers: ['Burgundy', 'Silver', 'Gold', 'Platinum'],
  },
  {
    name: 'SAS EuroBonus',
    category: 'airline',
    aliases: ['SAS', 'EuroBonus'],
    tiers: ['Silver', 'Gold', 'Diamond', 'Pandion'],
  },
  {
    name: 'Singapore Airlines KrisFlyer',
    category: 'airline',
    aliases: ['Singapore Airlines (KrisFlyer)'],
    tiers: ['KrisFlyer', 'Elite Silver', 'Elite Gold', 'PPS Club'],
  },
  {
    name: 'The British Airways Club',
    category: 'airline',
    aliases: ['British Airways', 'British Airways Executive Club'],
    tiers: ['Blue', 'Bronze', 'Silver', 'Gold', 'Gold Guest List'],
  },
  {
    name: 'Turkish Airlines Miles&Smiles',
    category: 'airline',
    aliases: ['Turkish Airlines', 'Miles&Smiles'],
    tiers: ['Elite', 'Elite Plus'],
  },
  {
    name: 'United Airlines MileagePlus',
    category: 'airline',
    aliases: ['United Airlines (MileagePlus)'],
    tiers: ['Premier Silver', 'Premier Gold', 'Premier Platinum', 'Premier 1K'],
  },
  {
    name: 'Virgin Atlantic Flying Club',
    category: 'airline',
    aliases: ['Virgin Atlantic'],
    tiers: ['Red', 'Silver', 'Gold'],
  },
  {
    name: 'Virgin Australia Velocity Frequent Flyer',
    category: 'airline',
    aliases: ['Virgin Australia Velocity', 'Virgin Australia', 'Velocity'],
    tiers: ['Red', 'Silver', 'Gold', 'Platinum', 'Platinum Plus'],
  },
];

const HOTEL_PROGRAMS: Array<LoyaltyProgramDefinition> = [
  {
    name: 'Accor Live Limitless',
    category: 'hotel',
    tiers: ['Classic', 'Silver', 'Gold', 'Platinum', 'Diamond'],
  },
  {
    name: 'Hilton Honors',
    category: 'hotel',
    tiers: ['Member', 'Silver', 'Gold', 'Diamond'],
  },
  {
    name: 'IHG One Rewards',
    category: 'hotel',
    tiers: ['Club', 'Silver Elite', 'Gold Elite', 'Platinum Elite', 'Diamond Elite', 'Spire Elite'],
  },
  {
    name: 'Marriott Bonvoy',
    category: 'hotel',
    tiers: ['Member', 'Silver Elite', 'Gold Elite', 'Platinum Elite', 'Titanium Elite', 'Ambassador Elite'],
  },
  {
    name: 'World of Hyatt',
    category: 'hotel',
    tiers: ['Member', 'Discoverist', 'Explorist', 'Globalist'],
  },
  {
    name: 'Wyndham Rewards',
    category: 'hotel',
    tiers: ['Blue', 'Gold', 'Platinum', 'Diamond'],
  },
];

const OTA_PROGRAMS: Array<LoyaltyProgramDefinition> = [
  {
    name: 'Booking.com Genius',
    category: 'ota',
    aliases: ['Booking.com'],
    tiers: ['Genius Level 1', 'Genius Level 2', 'Genius Level 3'],
  },
  {
    name: 'Expedia One Key',
    category: 'ota',
    aliases: ['Expedia'],
    tiers: ['Blue', 'Silver', 'Gold', 'Platinum'],
  },
  {
    name: 'Trip.com Rewards',
    category: 'ota',
    aliases: ['Trip.com'],
    tiers: ['Silver', 'Gold', 'Platinum', 'Diamond', 'Diamond+', 'Black Diamond'],
  },
  {
    name: 'World360 Rewards',
    category: 'ota',
    tiers: ['Member Plus'],
  },
];

/** Pre-populated loyalty programs and source tiers for Status Match campaigns. */
export const LOYALTY_PROGRAMS: Array<LoyaltyProgramDefinition> = [
  ...AIRLINE_PROGRAMS,
  ...HOTEL_PROGRAMS,
  ...OTA_PROGRAMS,
];

const CATEGORY_SORT_ORDER = Object.fromEntries(
  LOYALTY_PROGRAM_CATEGORY_ORDER.map((category, index) => [category, index]),
) as Record<LoyaltyProgramCategory, number>;

export function sortLoyaltyProgramsByCategory(
  programs: Array<LoyaltyProgramDefinition> = LOYALTY_PROGRAMS,
): Array<LoyaltyProgramDefinition> {
  return [...programs].sort((a, b) => {
    const byCategory = CATEGORY_SORT_ORDER[a.category] - CATEGORY_SORT_ORDER[b.category];
    if (byCategory !== 0) return byCategory;
    return a.name.localeCompare(b.name);
  });
}

export function getLoyaltyProgramNamesByCategory(): Array<string> {
  return sortLoyaltyProgramsByCategory().map((program) => program.name);
}

export function getLoyaltyProgramCategoryLabel(programName: string): string {
  const category = findProgramByName(programName)?.category;
  return category ? LOYALTY_PROGRAM_CATEGORY_LABELS[category] : '';
}

export function slugifyProgramName(programName: string): string {
  return programName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function defaultLogoImageId(programName: string): string {
  return `match-campaign-logo-${slugifyProgramName(programName)}`;
}

export function findProgramByName(name: string): LoyaltyProgramDefinition | undefined {
  return LOYALTY_PROGRAMS.find((program) => program.name === name || program.aliases?.some((alias) => alias === name));
}

export function resolveProgramDisplayName(name: string): string {
  return findProgramByName(name)?.name ?? name;
}
